import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as FormAction from '../store/form';
import * as apiUtil from '../../../util/apiUtil';
import Loading from '../../../component/loading/Loading';
import TemplateSelector from '../component/TemplateSelector';
import FormVariableInput from '../component/FormVariableInput';
import FormPreview from '../component/FormPreview';

/**
 * 폼 생성 컨테이너
 * - 페이지로 사용 가능하고, 모달로도 쉽게 전환 가능한 구조
 * - onComplete, onCancel props를 받아서 모달 모드로 전환 가능
 */
const FormCreateContainer = ({ onComplete, onCancel, isModal = false }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { templateList, selectedTemplate, formVariables, createdForm } = useSelector(state => state.form);

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: 템플릿 선택, 2: 변수 입력, 3: 완료

    // 템플릿 목록 조회
    const fetchTemplates = useCallback(() => {
        apiUtil.asyncRequest({
            setLoading,
            url: '/api/user/forms/templates',
            method: apiUtil.requestMethod.GET,
            success: (result) => {
                dispatch(FormAction.setTemplateList(result));
                console.log(result)
            }
        });
    }, [dispatch]);

    // 컴포넌트 마운트 시 템플릿 조회
    useEffect(() => {
        fetchTemplates();
        return () => {
            // 언마운트 시 초기화
            dispatch(FormAction.setSelectedTemplate(null));
            dispatch(FormAction.setCreatedForm(null));
        };
    }, [fetchTemplates, dispatch]);

    // 템플릿 선택 핸들러
    const handleTemplateSelect = (templateId) => {
        apiUtil.asyncRequest({
            setLoading,
            url: `/api/user/forms/templates/${templateId}`,
            method: apiUtil.requestMethod.GET,
            success: (result) => {
                dispatch(FormAction.setSelectedTemplate(result));
                setStep(2); // 다음 단계로
            }
        });
    };

    // 변수 입력 변경 핸들러
    const handleVariableChange = (variableName, value) => {
        const newVariables = {
            ...formVariables,
            [variableName]: value
        };
        dispatch(FormAction.setFormVariables(newVariables));
    };

    // 폼 생성 핸들러
    const handleCreateForm = () => {
        // 모든 변수가 입력되었는지 확인
        const variables = selectedTemplate?.variables || [];
        const missingVariables = variables.filter(v => !formVariables[v]);
        if (missingVariables.length > 0) {
            alert(`다음 항목을 입력해주세요: ${missingVariables.join(', ')}`);
            return;
        }

        apiUtil.asyncRequest({
            setLoading,
            url: '/api/user/forms',
            method: apiUtil.requestMethod.POST,
            data: {
                templateId: selectedTemplate.uid,
                variables: formVariables
            },
            success: (result) => {
                dispatch(FormAction.setCreatedForm(result));
                setStep(3); // 완료 단계

                if (isModal && onComplete) {
                    // 모달 모드일 때
                    onComplete(result);
                }
            },
            redirect_url: window.location.origin + '/form/list'
        });
    };

    // 취소 핸들러
    const handleCancel = () => {
        if (isModal && onCancel) {
            onCancel();
        } else {
            navigate('/form/list');
        }
    };

    // 목록으로 돌아가기
    const handleGoToList = () => {
        if (isModal && onComplete) {
            onComplete(createdForm);
        } else {
            navigate('/form/list');
        }
    };

    return (
        <>
            <Loading loading={loading} />
            <div className={isModal ? '' : 'container-xxl'}>
                {!isModal && (
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className='fs-4 mb-0'>설문 만들기</h2>
                        <button className="btn btn-secondary" onClick={handleCancel}>
                            취소
                        </button>
                    </div>
                )}

                <div className='card'>
                    <div className='card-body'>
                        {/* Step 1: 템플릿 선택 */}
                        {step === 1 && (
                            <>
                                <div className="alert alert-info d-flex align-items-start mb-3" role="alert">
                                    <i className="bi bi-info-circle-fill me-2 mt-1"></i>
                                    <div>
                                        원하시는 내용의 템플릿이 없는 경우, <strong>1:1문의</strong> 등록을 진행해 주세요.
                                    </div>
                                </div>
                                <TemplateSelector
                                    templateList={templateList}
                                    onSelect={handleTemplateSelect}
                                />
                            </>
                        )}

                        {/* Step 2: 변수 입력 */}
                        {step === 2 && selectedTemplate && (
                            <div>
                                <FormVariableInput
                                    selectedTemplate={selectedTemplate}
                                    variables={formVariables}
                                    onVariableChange={handleVariableChange}
                                />
                                <div className="d-flex justify-content-between mt-4">
                                    <button className="btn btn-secondary" onClick={() => setStep(1)}>
                                        ← 이전
                                    </button>
                                    <button className="btn btn-primary" onClick={handleCreateForm}>
                                        설문 생성
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: 완료 */}
                        {step === 3 && createdForm && (
                            <FormPreview
                                formData={createdForm}
                                onGoToList={handleGoToList}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default FormCreateContainer;
